import React, { useEffect, useState } from 'react';
import api from '../../services/api';

interface QueueJob {
  id: string;
  type: string;
  status: 'pending' | 'processing' | 'completed' | 'failed';
  createdAt: string;
  startedAt?: string;
  completedAt?: string;
  error?: string;
  retryCount: number;
  maxRetries: number;
  data?: any;
}

interface QueueStatus {
  active: boolean;
  pendingJobs: number;
  processingJobs: number;
  completedJobs: number;
  failedJobs: number;
  totalJobs: number;
  averageProcessingTime: number;
  lastCheck: string;
  recentJobs: QueueJob[];
}

/**
 * Enhanced Queue Dashboard Component
 * Provides real-time queue monitoring and retry management
 */
const QueueDashboard: React.FC = () => {
  const [queueStatus, setQueueStatus] = useState<QueueStatus | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [selectedJob, setSelectedJob] = useState<QueueJob | null>(null);

  const fetchQueueStatus = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const res = await api.get('/admin/queue/status');
      setQueueStatus(res.data.data);
    } catch (e: any) {
      setError(e?.message || 'Failed to load queue status');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchQueueStatus();
    
    if (autoRefresh) {
      const interval = setInterval(fetchQueueStatus, 5000); // Refresh every 5 seconds
      return () => clearInterval(interval);
    }
    return undefined;
  }, [autoRefresh]);

  const handleToggleQueue = async () => {
    if (!queueStatus) return;
    
    try {
      await api.post('/admin/queue/toggle', {
        active: !queueStatus.active
      });
      fetchQueueStatus();
    } catch (e: any) {
      setError(e?.message || 'Failed to toggle queue');
    }
  };

  const handleRetryJob = async (jobId: string) => {
    try {
      await api.post('/admin/queue/retry', { jobId });
      fetchQueueStatus();
    } catch (e: any) {
      setError(e?.message || 'Failed to retry job');
    }
  };

  const handleRetryAllFailed = async () => {
    try {
      await api.post('/admin/queue/retry', { all: true });
      fetchQueueStatus();
    } catch (e: any) {
      setError(e?.message || 'Failed to retry all failed jobs');
    }
  };

  const getJobStatusBadge = (status: string) => {
    const badges = {
      pending: 'bg-yellow-500 text-black',
      processing: 'bg-blue-500 text-white',
      completed: 'bg-green-500 text-white',
      failed: 'bg-red-500 text-white'
    };
    return badges[status as keyof typeof badges] || 'bg-gray-500 text-white';
  };

  const getJobStatusColor = (status: string) => {
    const colors = {
      pending: 'text-yellow-400',
      processing: 'text-blue-400',
      completed: 'text-green-400',
      failed: 'text-red-400'
    };
    return colors[status as keyof typeof colors] || 'text-gray-400';
  };

  const formatDuration = (startTime?: string, endTime?: string) => {
    if (!startTime) return '-';
    const start = new Date(startTime);
    const end = endTime ? new Date(endTime) : new Date();
    const duration = end.getTime() - start.getTime();
    const seconds = Math.floor(duration / 1000);
    const minutes = Math.floor(seconds / 60);
    const hours = Math.floor(minutes / 60);
    
    if (hours > 0) return `${hours}h ${minutes % 60}m`;
    if (minutes > 0) return `${minutes}m ${seconds % 60}s`;
    return `${seconds}s`;
  };

  const getQueueHealthStatus = () => {
    if (!queueStatus) return 'Unknown';
    if (queueStatus.failedJobs > queueStatus.totalJobs * 0.1) return 'Poor';
    if (queueStatus.failedJobs > queueStatus.totalJobs * 0.05) return 'Fair';
    return 'Good';
  };

  const getHealthBadge = (health: string) => {
    const badges = {
      Good: 'bg-green-500 text-white',
      Fair: 'bg-yellow-500 text-black',
      Poor: 'bg-red-500 text-white',
      Unknown: 'bg-gray-500 text-white'
    };
    return badges[health as keyof typeof badges] || 'bg-gray-500 text-white';
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold text-white">Queue Dashboard</h2>
          <p className="text-neutral-400">Real-time monitoring of job queue and processing</p>
        </div>
        <div className="flex items-center space-x-4">
          <label className="flex items-center">
            <input
              type="checkbox"
              checked={autoRefresh}
              onChange={(e) => setAutoRefresh(e.target.checked)}
              className="mr-2"
            />
            <span className="text-sm text-neutral-300">Auto-refresh</span>
          </label>
          <button
            onClick={fetchQueueStatus}
            className="btn btn-outline px-4 py-2"
          >
            Refresh
          </button>
          {queueStatus && (
            <button
              onClick={handleToggleQueue}
              className={`btn px-4 py-2 ${queueStatus.active ? 'btn-red' : 'btn-green'}`}
            >
              {queueStatus.active ? 'Stop Queue' : 'Start Queue'}
            </button>
          )}
        </div>
      </div>

      <div className="card bg-amber-900/30 border border-amber-500/60 p-4 flex items-start gap-3">
        <div className="w-10 h-10 rounded-full bg-amber-500/20 text-amber-300 flex items-center justify-center font-semibold">
          β
        </div>
        <div className="space-y-1">
          <p className="text-amber-200 font-semibold">Queue Processing Coming Soon</p>
          <p className="text-sm text-amber-100/80">
            The monitoring UI is ready, but no workloads enqueue jobs yet. Once background jobs
            start using the queue service you’ll see live metrics, retry controls, and job history here.
          </p>
        </div>
      </div>

      {error && (
        <div className="card bg-red-900/20 border border-red-500 p-4">
          <p className="text-red-400">{error}</p>
        </div>
      )}

      {loading ? (
        <div className="text-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-cyan-400 mx-auto mb-4"></div>
          <p className="text-neutral-400">Loading queue status...</p>
        </div>
      ) : queueStatus ? (
        <>
          {/* Queue Status Overview */}
          <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
            <div className="card bg-neutral-900 p-4">
              <h3 className="text-sm font-medium text-neutral-400 mb-2">Queue Status</h3>
              <div className="flex items-center space-x-2">
                <div className={`w-3 h-3 rounded-full ${queueStatus.active ? 'bg-green-400' : 'bg-red-400'}`}></div>
                <p className="text-lg font-bold text-white">
                  {queueStatus.active ? 'Active' : 'Inactive'}
                </p>
              </div>
            </div>
            <div className="card bg-neutral-900 p-4">
              <h3 className="text-sm font-medium text-neutral-400 mb-2">Pending Jobs</h3>
              <p className="text-2xl font-bold text-yellow-400">{queueStatus.pendingJobs}</p>
            </div>
            <div className="card bg-neutral-900 p-4">
              <h3 className="text-sm font-medium text-neutral-400 mb-2">Processing</h3>
              <p className="text-2xl font-bold text-blue-400">{queueStatus.processingJobs}</p>
            </div>
            <div className="card bg-neutral-900 p-4">
              <h3 className="text-sm font-medium text-neutral-400 mb-2">Completed</h3>
              <p className="text-2xl font-bold text-green-400">{queueStatus.completedJobs}</p>
            </div>
            <div className="card bg-neutral-900 p-4">
              <h3 className="text-sm font-medium text-neutral-400 mb-2">Failed</h3>
              <p className="text-2xl font-bold text-red-400">{queueStatus.failedJobs}</p>
            </div>
          </div>

          {/* Queue Health and Performance */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="card bg-neutral-900 p-4">
              <h3 className="text-sm font-medium text-neutral-400 mb-2">Queue Health</h3>
              <div className="flex items-center space-x-2">
                <span className={`px-2 py-1 text-xs rounded-full ${getHealthBadge(getQueueHealthStatus())}`}>
                  {getQueueHealthStatus()}
                </span>
              </div>
            </div>
            <div className="card bg-neutral-900 p-4">
              <h3 className="text-sm font-medium text-neutral-400 mb-2">Avg Processing Time</h3>
              <p className="text-lg font-bold text-white">
                {queueStatus.averageProcessingTime > 0 
                  ? `${queueStatus.averageProcessingTime.toFixed(0)}ms`
                  : 'N/A'
                }
              </p>
            </div>
            <div className="card bg-neutral-900 p-4">
              <h3 className="text-sm font-medium text-neutral-400 mb-2">Success Rate</h3>
              <p className="text-lg font-bold text-white">
                {queueStatus.totalJobs > 0 
                  ? `${((queueStatus.completedJobs / queueStatus.totalJobs) * 100).toFixed(1)}%`
                  : '0%'
                }
              </p>
            </div>
          </div>

          {/* Failed Jobs Retry Section */}
          {queueStatus.failedJobs > 0 && (
            <div className="card bg-neutral-900 p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-white">Failed Jobs</h3>
                <button
                  onClick={handleRetryAllFailed}
                  className="btn btn-outline px-4 py-2"
                >
                  Retry All Failed
                </button>
              </div>
              <div className="space-y-2">
                {queueStatus.recentJobs
                  .filter(job => job.status === 'failed')
                  .slice(0, 5)
                  .map((job) => (
                    <div key={job.id} className="flex items-center justify-between p-3 bg-neutral-800 rounded-lg">
                      <div className="flex-1">
                        <div className="flex items-center space-x-3">
                          <span className={`px-2 py-1 text-xs rounded-full ${getJobStatusBadge(job.status)}`}>
                            {job.status.toUpperCase()}
                          </span>
                          <span className="text-white font-medium">{job.type}</span>
                          <span className="text-neutral-400 text-sm">
                            Retries: {job.retryCount}/{job.maxRetries}
                          </span>
                        </div>
                        {job.error && (
                          <p className="text-red-400 text-sm mt-1">{job.error}</p>
                        )}
                        <p className="text-neutral-500 text-xs mt-1">
                          Created: {new Date(job.createdAt).toLocaleString()}
                        </p>
                      </div>
                      <button
                        onClick={() => handleRetryJob(job.id)}
                        className="btn btn-outline btn-sm px-3 py-1"
                      >
                        Retry
                      </button>
                    </div>
                  ))}
              </div>
            </div>
          )}

          {/* Recent Jobs Table */}
          <div className="card bg-neutral-900 p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-white">Recent Jobs</h3>
              <span className="text-sm text-neutral-400">
                Last updated: {new Date(queueStatus.lastCheck).toLocaleString()}
              </span>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-neutral-300 border-b border-neutral-800">
                    <th className="py-3 pr-4">Job ID</th>
                    <th className="py-3 pr-4">Type</th>
                    <th className="py-3 pr-4">Status</th>
                    <th className="py-3 pr-4">Duration</th>
                    <th className="py-3 pr-4">Retries</th>
                    <th className="py-3 pr-4">Created</th>
                    <th className="py-3 pr-4">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-neutral-800">
                  {queueStatus.recentJobs.map((job) => (
                    <tr key={job.id} className="hover:bg-neutral-800">
                      <td className="py-3 pr-4 text-neutral-300 font-mono text-xs">
                        {job.id.substring(0, 8)}...
                      </td>
                      <td className="py-3 pr-4 text-white">{job.type}</td>
                      <td className="py-3 pr-4">
                        <span className={`px-2 py-1 text-xs rounded-full ${getJobStatusBadge(job.status)}`}>
                          {job.status.toUpperCase()}
                        </span>
                      </td>
                      <td className={`py-3 pr-4 ${getJobStatusColor(job.status)}`}>
                        {formatDuration(job.startedAt, job.completedAt)}
                      </td>
                      <td className="py-3 pr-4 text-neutral-300">
                        {job.retryCount}/{job.maxRetries}
                      </td>
                      <td className="py-3 pr-4 text-neutral-300">
                        {new Date(job.createdAt).toLocaleString()}
                      </td>
                      <td className="py-3 pr-4">
                        {job.status === 'failed' && (
                          <button
                            onClick={() => handleRetryJob(job.id)}
                            className="text-blue-400 hover:text-blue-300 text-sm"
                          >
                            Retry
                          </button>
                        )}
                        {job.status === 'completed' && (
                          <button
                            onClick={() => setSelectedJob(job)}
                            className="text-neutral-400 hover:text-neutral-300 text-sm"
                          >
                            View
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      ) : (
        <div className="text-center py-8">
          <p className="text-neutral-400">No queue data available</p>
        </div>
      )}

      {/* Job Details Modal */}
      {selectedJob && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-neutral-900 p-6 rounded-lg max-w-2xl w-full mx-4">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold text-white">Job Details</h3>
              <button
                onClick={() => setSelectedJob(null)}
                className="text-neutral-400 hover:text-neutral-300"
              >
                ×
              </button>
            </div>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-neutral-400 mb-1">Job ID</label>
                <p className="text-white font-mono text-sm">{selectedJob.id}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-neutral-400 mb-1">Type</label>
                <p className="text-white">{selectedJob.type}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-neutral-400 mb-1">Status</label>
                <span className={`px-2 py-1 text-xs rounded-full ${getJobStatusBadge(selectedJob.status)}`}>
                  {selectedJob.status.toUpperCase()}
                </span>
              </div>
              <div>
                <label className="block text-sm font-medium text-neutral-400 mb-1">Duration</label>
                <p className="text-white">{formatDuration(selectedJob.startedAt, selectedJob.completedAt)}</p>
              </div>
              {selectedJob.error && (
                <div>
                  <label className="block text-sm font-medium text-neutral-400 mb-1">Error</label>
                  <p className="text-red-400 text-sm">{selectedJob.error}</p>
                </div>
              )}
              {selectedJob.data && (
                <div>
                  <label className="block text-sm font-medium text-neutral-400 mb-1">Data</label>
                  <pre className="text-white text-sm bg-neutral-800 p-2 rounded overflow-auto">
                    {JSON.stringify(selectedJob.data, null, 2)}
                  </pre>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default QueueDashboard;
